// ANCHOR React
import { useCallback, useMemo } from 'react';

// ANCHOR Base
import { Button } from 'baseui/button';
import { Block } from 'baseui/block';

// ANCHOR Next
import { useRouter } from 'next/router';

// ANCHOR Models
import {
  useCourseDataId,
  useCourseDataPrice,
  useCourseDataArchived,
  useCourseDataPublishStatus,
  useCourseDataPaymentLink,
} from '@lyon/scoped-models/CourseData';
import { ProviderCheckManager } from '@lyon/scoped-models/ProviderCheckManager';
import { useCourseInstructorCheckData } from '@lyon/scoped-models/CourseInstructorCheck';
import { useCourseModulesData } from '@lyon/scoped-models/CourseModulesData';

// ANCHOR Hooks
import { useIsomorphicEffect } from '@lyon/utils/hooks/useIsomorphicEffect';
import { useSentryExceptionHandler } from '@lyon/utils/hooks/useSentryExceptionHandler';

// ANCHOR Components
import { ContentBuy, IContentBuyOverrides } from '@lyon/components/utils/ContentBuy';
import { PublishStatus } from '@lyon/utils/api/results/course';
import { LyonLink } from '../LyonLink';

// ANCHOR Constants
import { DISABLE_MESSAGE } from './constants';

interface IProps {
  initialPrice?: number;
  overrides?: IContentBuyOverrides;
}

export function CourseBuyButton({ overrides, initialPrice }: IProps) {
  const catchException = useSentryExceptionHandler();
  const router = useRouter();

  const courseId = useCourseDataId();
  const coursePrice = useCourseDataPrice();
  const paymentLink = useCourseDataPaymentLink();
  const isArchived = useCourseDataArchived();
  const publishStatus = useCourseDataPublishStatus();
  const isProvider = ProviderCheckManager.useSelector((state) => state.data);
  const isCourseInstructor = useCourseInstructorCheckData();
  const modules = useCourseModulesData();
  const courseContent = useMemo(() => {
    if (courseId) {
      const firstModuleWithItem = modules?.find((item) => item.moduleItems.length > 0);
      const firstModuleItem = firstModuleWithItem?.moduleItems[0];

      const statusParam = router.query.status;
      const courseStatus = typeof statusParam === 'string'
        ? `?status=${statusParam}`
        : '';

      return `/course/${courseId}/content${firstModuleItem ? `/${firstModuleItem.id}` : ''}${courseStatus}`;
    }

    return undefined;
  }, [courseId, modules, router.query.status]);

  const price = initialPrice ?? coursePrice;

  const onClick = useCallback(async () => {
    if (publishStatus) {
      const isBuyAllowed = [PublishStatus.PUBLISHED, PublishStatus.UNLISTED]
        .includes(publishStatus);

      if (courseId && !isArchived && isBuyAllowed) {
        await router.push(`/course-payment/${courseId}`);
      }
    }
  }, [publishStatus, courseId, isArchived, router]);

  useIsomorphicEffect(() => {
    if (publishStatus) {
      const isBuyAllowed = [PublishStatus.PUBLISHED, PublishStatus.UNLISTED]
        .includes(publishStatus);

      if (courseId && !isArchived && isBuyAllowed) {
        router
          .prefetch(`/course-payment/${courseId}`)
          .catch((err) => {
            catchException(err);
          });
      }
    }
  }, [router, courseId, isArchived, publishStatus, price]);

  if (!publishStatus) {
    return null;
  }

  return (
    <Block display="flex">
      {
        isCourseInstructor
          ? (
            <LyonLink href={`/course/${courseId ?? ''}/dashboard`}>
              <Button
                type="button"
                size="default"
              >
                Enter Dashboard
              </Button>
            </LyonLink>
          )
          : (
            <ContentBuy
              price={price}
              paymentLink={paymentLink}
              onClick={onClick}
              isLoading={typeof price === 'undefined' && !courseId}
              isRestricted={isArchived || [PublishStatus.DRAFT, PublishStatus.CLOSED]
                .includes(publishStatus)}
              restrictedMessage={PublishStatus.CLOSED && DISABLE_MESSAGE}
              overrides={overrides}
            />
          )
      }
      {
        (isProvider || isCourseInstructor) && courseContent && (
          <Block marginLeft="16px">
            <LyonLink href={courseContent}>
              <Button
                type="button"
                size="default"
              >
                Enter Course
              </Button>
            </LyonLink>
          </Block>
        )
      }
    </Block>
  );
}
